import axios from "axios";

// Netplus "Customer Status Report" — mutation + download helpers.
// GET reads live in appApi.js (RTK Query). Auth + x-project-id are added by the
// global axios interceptor; every request also carries ?partnerCode=, passed
// in by the caller (the pages hold the selected partner in localStorage —
// see usePartnerCode in the page config).

const API = `${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/netplus/customer-status`;

const withPartner = (partnerCode, params = {}) => ({
  params: { partnerCode, ...params },
});

export const createPartnerApi = ({ partnerCode, projectId, partnerName, location }) =>
  axios.post(`${API}/partners`, { partnerCode, projectId, partnerName, location });

// `resolutions` ({ "<partner>|<userId>": "<actual partner>" }) answers the
// duplicate-id check: when a customer id in the sheet already exists under a
// different partner the server replies 409 { requiresResolution, conflicts }
// without writing anything, and the upload is re-sent with the user's choices.
export const uploadCustomerStatusSheet = ({ file, snapshotDate, partnerCode, resolutions }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("partnerCode", partnerCode);
  fd.append("snapshotDate", snapshotDate);
  if (resolutions) fd.append("resolutions", JSON.stringify(resolutions));
  return axios.post(`${API}/uploads`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const rerunDiffApi = (uploadId, partnerCode) =>
  axios.post(`${API}/uploads/${uploadId}/rerun-diff`, null, withPartner(partnerCode));

// Snapshot storage (super-admin) — one-time move of the per-day snapshot rows
// from the database to files on the server. `copy` is time-boxed on the
// backend: call it until `pending` reaches 0.
export const getSnapshotStorageApi = () => axios.get(`${API}/snapshot-storage`);
export const copySnapshotsToFilesApi = () => axios.post(`${API}/snapshot-storage/copy`);
export const dropSnapshotCollectionApi = () =>
  axios.post(`${API}/snapshot-storage/drop-collection`);

// Batch upload — any number of files, each possibly a multi-tab workbook.
// No partnerCode is sent: every sheet/tab is matched to its partner by its
// own banner text on the backend; unmatched tabs come back in `results` as
// skipped rather than blocking the rest of the batch.
export const bulkUploadSheetsApi = ({ files, snapshotDate, resolutions }) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  fd.append("snapshotDate", snapshotDate);
  if (resolutions) fd.append("resolutions", JSON.stringify(resolutions));
  return axios.post(`${API}/uploads/bulk`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const assignFollowupApi = (id, assignedTo, partnerCode) =>
  axios.patch(`${API}/followups/${id}/assign`, { assignedTo }, withPartner(partnerCode));

export const logFollowupCallApi = (id, payload, partnerCode) =>
  axios.post(`${API}/followups/${id}/calls`, payload, withPartner(partnerCode));

export const markFollowupPaidApi = (id, note, partnerCode) =>
  axios.post(`${API}/followups/${id}/mark-paid`, { note }, withPartner(partnerCode));

export const addCustomerNoteApi = (customerKey, note, partnerCode) =>
  axios.post(
    `${API}/customers/${customerKey}/notes`,
    { note },
    withPartner(partnerCode),
  );

// Streamed .xlsx — fetched as a blob so the Authorization header rides along,
// then handed to the browser as a download.
export const downloadWorkbook = async (partnerCode, params = {}) => {
  const res = await axios.get(`${API}/export`, {
    ...withPartner(partnerCode, params),
    responseType: "blob",
  });
  const disposition = res.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : "netplus-customer-status.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const fetchDigestHtml = (partnerCode) =>
  axios.get(`${API}/digest`, withPartner(partnerCode, { format: "html" })).then((r) => r.data);

// ── Team roster (add / remove is super-admin) ──────────────────────────────
export const addTeamMemberApi = (userId, role = "") =>
  axios.post(`${API}/team`, { userId, role });
export const updateTeamMemberApi = (id, payload) =>
  axios.patch(`${API}/team/${id}`, payload);
export const removeTeamMemberApi = (id) => axios.delete(`${API}/team/${id}`);

// ── Modem-recovery cases ──────────────────────────────────────────────────
// `transferredOn` is super-admin only server-side — anyone else's value is
// ignored and the transfer is dated to now.
export const transferToModemRecoveryApi = ({
  partnerCode,
  customerKey,
  reason,
  assignedTo,
  transferredOn,
}) =>
  axios.post(
    `${API}/modem-recovery`,
    { partnerCode, customerKey, reason, assignedTo, transferredOn },
    withPartner(partnerCode),
  );
export const setModemOutcomeApi = (id, payload, partnerCode) =>
  axios.patch(`${API}/modem-recovery/${id}/outcome`, payload, withPartner(partnerCode));
export const addModemRemarkApi = (id, text, partnerCode) =>
  axios.post(`${API}/modem-recovery/${id}/remarks`, { text }, withPartner(partnerCode));
// Mark a case Untraceable — reassigns it to `assignedTo` (required) instead of
// closing it; `note`/`photo` are both optional.
export const markCaseUntraceableApi = (id, { note, photo, assignedTo }, partnerCode) => {
  const fd = new FormData();
  fd.append("note", note || "");
  fd.append("assignedTo", assignedTo);
  if (photo) fd.append("photo", photo);
  return axios.post(`${API}/modem-recovery/${id}/untraceable`, fd, {
    ...withPartner(partnerCode),
    headers: { "Content-Type": "multipart/form-data" },
  });
};
// `handedOverAt` is super-admin only server-side — anyone else's value is
// ignored and the hand-over is dated to now.
export const handoverModemCaseApi = (id, note, partnerCode, handedOverAt) =>
  axios.post(
    `${API}/modem-recovery/${id}/handover`,
    { note: note || "", handedOverAt },
    withPartner(partnerCode),
  );
export const handoverModemCasesBulkApi = (caseIds, note, partnerCode, handedOverAt) =>
  axios.post(
    `${API}/modem-recovery/handover/bulk`,
    { caseIds, note: note || "", handedOverAt },
    withPartner(partnerCode),
  );
// Undo a wrong transfer — removes the case and puts the customer back on the
// normal customer / follow-up flow.
export const cancelModemTransferApi = (id, partnerCode) =>
  axios.post(`${API}/modem-recovery/${id}/cancel-transfer`, null, withPartner(partnerCode));
export const reopenModemCaseApi = (id, partnerCode) =>
  axios.post(`${API}/modem-recovery/${id}/reopen`, null, withPartner(partnerCode));
export const bulkTransferToModemRecoveryApi = ({
  partnerCode,
  customerKeys,
  reason,
  assignedTo,
  transferredOn,
}) =>
  axios.post(
    `${API}/modem-recovery/bulk`,
    { partnerCode, customerKeys, reason, assignedTo, transferredOn },
    withPartner(partnerCode),
  );
// Blank starter workbook for the Excel bulk-action upload (User Id + Action +
// Remark columns).
export const downloadModemActionTemplate = async (partnerCode) => {
  const res = await axios.get(`${API}/modem-recovery/bulk-action-template`, {
    ...withPartner(partnerCode),
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "modem-recovery-bulk-action-template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// One upload, one shared template — each row is either Action "transfer"
// (open a case) or "recover" (close an open case as recovered). Returns
// { created, createdTransfer, createdRecover, skipped: [{row, userId, reason}] }.
export const bulkModemActionUploadApi = ({ file, partnerCode }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("partnerCode", partnerCode);
  return axios.post(`${API}/modem-recovery/bulk-action-upload`, fd, {
    ...withPartner(partnerCode),
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Single payment, logged directly from a customer row ("Add Payment" button).
export const addFreelancerPaymentApi = ({
  partnerCode,
  customerKey,
  customerName,
  name,
  amount,
}) =>
  axios.post(
    `${API}/freelancer-payments`,
    { partnerCode, customerKey, customerName, name, amount },
    withPartner(partnerCode),
  );

// Freelancer payment ledger — web only (no bulk upload on mobile).
export const downloadFreelancerPaymentTemplate = async (partnerCode) => {
  const res = await axios.get(`${API}/freelancer-payments/template`, {
    ...withPartner(partnerCode),
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "freelancer-payments-template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Two columns only: Name (required), Amount (required, > 0). Returns
// { created, skipped: [{row, name, reason}] }.
export const bulkUploadFreelancerPaymentsApi = ({ file, partnerCode }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("partnerCode", partnerCode);
  return axios.post(`${API}/freelancer-payments/bulk-upload`, fd, {
    ...withPartner(partnerCode),
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const recoverFromTerminatedApi = ({
  partnerCode,
  customerKey,
  macId,
  note,
  transferredOn,
}) =>
  axios.post(
    `${API}/modem-recovery/from-terminated`,
    { partnerCode, customerKey, macId, note, transferredOn },
    withPartner(partnerCode),
  );

// ── Field-sheet reconciliation (super-admin only, local-disk storage) ─────
// No partnerCode — matching runs across every partner (a field sheet isn't
// scoped to one).
export const uploadReconciliationApi = ({ file }) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/reconciliation`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const listReconciliationRunsApi = () => axios.get(`${API}/reconciliation`);
export const getReconciliationRunApi = (runId) => axios.get(`${API}/reconciliation/${runId}`);
export const deleteReconciliationRunApi = (runId) =>
  axios.delete(`${API}/reconciliation/${runId}`);
export const applyReconciliationRowApi = (runId, rowIndex, payload) =>
  axios.post(`${API}/reconciliation/${runId}/rows/${rowIndex}/apply`, payload);
export const actReconciliationRowApi = (runId, rowIndex, payload) =>
  axios.post(`${API}/reconciliation/${runId}/rows/${rowIndex}/act`, payload);
export const downloadReconciliationExcel = async (runId) => {
  const res = await axios.get(`${API}/reconciliation/${runId}/export`, {
    responseType: "blob",
  });
  const match = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : "reconciliation.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ── Performance Excel (super-admin) ───────────────────────────────────────
export const downloadPerformanceExcel = async (partnerCode, { from, to } = {}) => {
  const res = await axios.get(`${API}/performance/export`, {
    ...withPartner(partnerCode, { from, to }),
    responseType: "blob",
  });
  const match = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : "netplus-performance.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ── Incentive revenue (super-admin) ─────────────────────────────────────────
export const setIncentivePlanPriceApi = (plan, amountInclGst) =>
  axios.put(`${API}/incentive/plans`, { plan, amountInclGst });

export const updateIncentiveConfigApi = (payload) =>
  axios.put(`${API}/incentive/config`, payload);

// Super-admin: delete one partner's copy of a customer (used for duplicates).
export const deleteCustomerApi = (partnerCode, customerKey) =>
  axios.delete(`${API}/customers/${encodeURIComponent(customerKey)}`, withPartner(partnerCode));
